<template>
  <div class="contents-list pt-5">
    <template v-if="contents && contents.length > 0">
      <ul class="list-container">
        <li
          v-for="content in contents"
          :key="content.id"
          :class="`content clickable`"
        >
          <div class="box-list" :class="`type-${content.type}`">
            <h4 class="content-title">
              <label :for="content.id">{{
                (content.title || "") | truncate(titleLength)
              }}</label>
              <input
                type="checkbox"
                v-model="checkedIds"
                :id="content.id"
                :value="content.id"
              />
            </h4>
            <div @click="() => onEditContent(content.id, content.type)">
              <div class="content-thumbnail">
                <img
                  :src="
                    content.thumbnailUrl ? content.thumbnailUrl : images.picture
                  "
                  :alt="content.title"
                />
              </div>
              <div class="content-detail">
                <div class="content-updated-time">
                  Last updated {{ getReadableDatetime(content.updatedAt) }}
                </div>
                <div
                  class="content-attribute"
                  v-if="
                    content.orderedCategories &&
                    content.orderedCategories.length > 0
                  "
                >
                  <div class="content-attribute-label">Categories</div>
                  <div class="content-attribute-value">
                    {{ getOrderedCategories(content.orderedCategories) }}
                  </div>
                </div>
                <div
                  class="content-attribute"
                  v-if="content.country || content.region || content.district"
                >
                  <div class="content-attribute-label">Location</div>
                  <div class="content-attribute-value">
                    {{ getLocationList(content) }}
                  </div>
                </div>
                <div
                  class="content-connections content-attribute"
                  v-if="content.contentConnections"
                >
                  <div class="content-attribute-label">Connections</div>
                  <div class="content-connections-detail">
                    <div class="connections-item">
                      <div class="content-attribute-label">Experiences</div>
                      <div class="content-attribute-value">
                        {{ content.contentConnections.experiences }}
                      </div>
                    </div>
                    <div class="connections-item">
                      <div class="content-attribute-label">Tours</div>
                      <div class="content-attribute-value">
                        {{ content.contentConnections.tours }}
                      </div>
                    </div>
                    <div class="connections-item">
                      <div class="content-attribute-label">Offers</div>
                      <div class="content-attribute-value">
                        {{ content.contentConnections.offers }}
                      </div>
                    </div>
                    <div class="connections-item">
                      <div class="content-attribute-label">Events</div>
                      <div class="content-attribute-value">
                        {{ content.contentConnections.events }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="content-affiliation-connections content-attribute">
                  <div class="content-attribute-label">Affiliations</div>
                  <div class="content-attribute-value">
                    {{ content.affiliationConnections.confirmed }} confirmed,
                    {{ content.affiliationConnections.pending }} pending,
                    {{ content.affiliationConnections.rejected }} rejected
                  </div>
                </div>
                <div class="content-platform-connections content-attribute">
                  <div class="content-attribute-label">Distributions</div>
                  <div class="content-attribute-value">
                    {{ content.platformConnections.confirmed }} confirmed,
                    {{ content.platformConnections.pending }} pending,
                    {{ content.platformConnections.rejected }} rejected
                  </div>
                </div>
                <div class="content-hashtag">
                  {{
                    (content.socialIdentifiers || "")
                      | truncate(socialIdentifiersLength)
                  }}
                </div>
              </div>
              <div class="content-meta"></div>
              <div :class="`content-status status-${content.status}`">
                <img
                  :src="
                    content.status === 'publish'
                      ? images.publishIcon
                      : images.editIcon
                  "
                />
              </div>
            </div>
          </div>
        </li>
      </ul>
    </template>

    <div v-else class="empty-results">
      <p>{{ emptyText }}</p>
    </div>

    <div>
      <b-pagination
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        first-number
        last-number
        align="center"
      >
        <template v-slot:prev-text
          ><span
            ><i class="fas fa-caret-left"></i>
            <span class="text-pagination">PREVIOUS</span></span
          ></template
        >
        <template v-slot:next-text
          ><span
            ><span class="text-pagination">NEXT</span>
            <i class="fas fa-caret-right"></i></span
        ></template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import publishIcon from "@/assets/images/publish-icon.svg";
import editIcon from "@/assets/images/edit-icon.svg";
import picture from "@/assets/images/picture.svg";

export default {
  props: {
    titleLength: {
      type: Number,
      default: 100,
    },
    descriptionLength: {
      type: Number,
      default: 200,
    },
    socialIdentifiersLength: {
      type: Number,
      default: 100,
    },
    types: {
      type: Array,
      default() {
        return [];
      },
    },
    emptyText: {
      type: String,
      default:
        "This account has no experience yet. Please press the ADD button above.",
    },
    search: {
      type: String,
      default: "",
    },
    sortBy: {
      type: String,
      default: "",
    },
    sortAscending: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: [],
    },
  },
  watch: {
    checkedIds: function (val) {
      this.$emit("input", val);
    },
  },
  data() {
    return {
      images: {
        publishIcon,
        editIcon,
        picture,
      },
      contents: [],
      checkedIds: this.value,
      next: "",
      nextToken: "",
      limit: 12,
      rows: 0,
      perPage: 1,
      currentPage: 1,
    };
  },
  apollo: {
    userContents: {
      query: require("../../graphql/queries/userContents.gql"),
      variables() {
        return {
          limit: this.limit,
          next: this.next,
          types: this.types,
          page: this.currentPage,
          search: this.search,
          sortBy: this.sortBy,
          sortAscending: this.sortAscending,
        };
      },
      result({ data }) {
        this.clear();
        const { results, next, totalPages } = data.userContents;
        this.contents = results;
        this.nextToken = next;
        this.rows = totalPages;
      },
      fetchPolicy: "network-only",
    },
  },
  methods: {
    loadmore() {
      this.next = this.nextToken;
    },
    reload() {
      this.$apollo.queries.userContents.refetch();
    },
    clear() {
      this.next = "";
      this.nextToken = "";
      this.contents = [];
    },
    onEditContent(id, type) {
      this.$emit("edit", id, type);
    },
    getReadableDatetime(time) {
      return moment(time).fromNow();
    },
    getOrderedCategories(categories) {
      return categories.map((it) => it.title).join(" | ");
    },
    getLocationList(item) {
      const list = [];
      if (item.country) {
        list.push(item.country.title);
      }
      if (item.region) {
        list.push(item.region.title);
      }
      if (item.district) {
        list.push(item.district.title);
      }
      return list.join(" | ");
    },
  },
};
</script>

<style scoped lang="scss">
.contents-list {
  .filters-panel {
    margin: 30px 0;
    display: flex;
    justify-content: flex-end;
  }

  .empty-results {
    margin: 100px auto 0;
  }

  .list-container {
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    padding: 0;

    .content {
      width: 25%;
      padding: 0 10px 10px 10px;
      color: black;

      .box-list {
        background-color: #fff;
        position: relative;
        height: 100%;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

        .content-thumbnail {
          width: 100%;
          flex: 0 0 auto;

          img {
            width: 100%;
            height: 150px;
            display: block;
            object-fit: cover;
            object-position: center;
          }
        }

        .content-detail {
          width: 100%;
          flex: 1 1 auto;
          padding: 6px;
          display: flex;
          flex-direction: column;
          font-size: 0.55rem;
          font-weight: 400;
          color: var(--color-gray);
        }

        .content-updated-time {
          color: var(--color-light-gray);
          margin-bottom: 6px;
          font-size: 0.625rem;
        }

        .content-hashtag {
          width: 100%;
          font-weight: 500;
          font-size: 0.625rem;
          color: black;
          margin-top: 5px;
        }

        .content-meta {
          width: 100%;
          padding: 20px 20px 20px 10px;
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
        }

        .content-attribute {
          display: flex;
          margin-top: 5px;

          .content-attribute-label {
            font-weight: bold;
            flex: 0 0 auto;
            margin-right: 5px;
          }
        }

        .content-connections {
          display: flex;

          .content-connections-detail {
            margin-left: 30px;
          }

          .connections-item {
            display: grid;
            grid-template-columns: 60px auto;
            column-gap: 5px;

            .content-attribute-label {
              flex: 0 0 auto;
            }
          }
        }

        .content-status {
          width: 100%;
          height: 30px;
          background-color: #58595b;
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: absolute;
          bottom: 0;
          display: flex;
          justify-content: center;
          align-items: center;

          &.status-publish {
            background-color: var(--primary-color);
          }

          img {
            width: 24px;
          }
        }
        .content-title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px;
          margin: 0;
          font-size: 12px;
          color: #fff;
          font-weight: 400;

          label {
            width: 100%;
            cursor: pointer;
            padding: 0;
            margin: 0;
          }
        }

        &.type-experience {
          .content-title {
            background-color: #1da1f2;
          }
        }

        &.type-tour {
          .content-title {
            background-color: #c32aa3;
          }
        }

        &.type-event {
          .content-title {
            background-color: #bd081c;
          }
        }

        &.type-offer {
          .content-title {
            background-color: #1877f2;
          }
        }

        &.type-campaign {
          .content-title {
            background-color: #c32aa3;
          }
        }
        &.type-story {
          .content-title {
            background-color: #1877f2;
          }
        }
      }
    }

    @media only screen and (max-width: 1400px) {
      .content {
        width: 33.33%;
      }
    }

    @media only screen and (max-width: 992px) {
      .content {
        width: 50%;
      }
    }

    @media only screen and (max-width: 480px) {
      .content {
        width: 100%;
      }
    }
  }
}
@media only screen and (max-width: $breakpoint-tablet) {
  .contents-list {
    justify-content: flex-start;
    .filters-panel {
      .search-form {
        input {
          width: 100%;
        }
      }
    }
  }
}
</style>
